import { useState, type CSSProperties } from "react";
import { useLocation } from "react-router-dom";
import { Sidebar } from "./sidebar";
import { Header } from "./header";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { AiAssistantPanel, AiAssistantTrigger } from "@/components/ai-assistant";
import { useCanUseAiAssistant } from "@/hooks/usePermission";

/**
 * Страницы, которые занимают всю высоту экрана и не нуждаются в нижнем паддинге
 */
const FULL_HEIGHT_PAGES = ["/telegram-client"];

interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Main layout component with sidebar, header and AI assistant
 */
export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isAiPanelOpen, setIsAiPanelOpen] = useState(false);
  const { canUse: canUseAiAssistant } = useCanUseAiAssistant();

  const isFullHeightPage = FULL_HEIGHT_PAGES.includes(location.pathname);

  const containerStyle: CSSProperties = {
    "--sidebar-offset": isCollapsed ? "70px" : "256px",
    "--ai-panel-offset": isAiPanelOpen && canUseAiAssistant ? "400px" : "0px",
  } as React.CSSProperties;

  return (
    <div
      className="flex h-screen overflow-hidden bg-background"
      style={containerStyle}
    >
      {/* Desktop Sidebar */}
      <div className="hidden lg:block fixed left-0 top-0 bottom-0 z-40">
        <Sidebar
          isCollapsed={isCollapsed}
          onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
        />
      </div>

      {/* Mobile Sidebar (Sheet) */}
      <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
        <SheetContent side="left" className="p-0 w-72">
          <Sidebar isMobile />
        </SheetContent>
      </Sheet>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <Header
          onMenuClick={() => setIsMobileOpen(true)}
          onAiAssistantClick={canUseAiAssistant ? () => setIsAiPanelOpen(!isAiPanelOpen) : undefined}
          className="fixed top-0 left-0 right-0 z-30 lg:left-[var(--sidebar-offset)] lg:right-[var(--ai-panel-offset)] transition-all duration-300"
        />

        {/* Page content */}
        <main
          data-scroll-container
          className={
          isFullHeightPage
            ? "flex-1 overflow-hidden pt-16 lg:pl-[var(--sidebar-offset)] lg:pr-[var(--ai-panel-offset)] transition-[padding-left,padding-right] duration-300"
            : "flex-1 overflow-y-auto px-4 pt-16 pb-8 lg:px-6 lg:pl-[calc(var(--sidebar-offset)+24px)] lg:pr-[calc(var(--ai-panel-offset)+24px)] transition-[padding-left,padding-right] duration-300"
        }>
          {children}
        </main>
      </div>

      {/* AI Assistant Panel - только при наличии права */}
      {canUseAiAssistant && (
        <>
          <AiAssistantPanel
            isOpen={isAiPanelOpen}
            onOpenChange={setIsAiPanelOpen}
          />

          {/* Floating Action Button для мобильных (когда панель закрыта) */}
          {!isAiPanelOpen && (
            <div className="lg:hidden">
              <AiAssistantTrigger
                variant="fab"
                onClick={() => setIsAiPanelOpen(true)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
